import { plural } from "../plural";

/**
 * English strings for the agent's activity in the chat: tool rows, group
 * headers, plan cards, the cards a tool paints (images, publish, connect,
 * secrets, files) and the turn's own chrome ("Worked for …").
 *
 * Only our words live here. File names, commands, search queries and anything
 * the model wrote are shown as they are.
 */
export default {
  /** The first word of a tool row: "Read App.tsx", "Run npm install". */
  verbs: {
    read: "Read",
    write: "Write",
    edit: "Edit",
    run: "Run",
    find: "Find",
    search: "Search",
    updateTodos: "Update todos",
    createPlan: "Create plan",
    updatePlan: "Update plan",
    createTask: "Create task",
    updateTask: "Update task",
    listTasks: "List tasks",
    getTask: "Get task",
    ask: "Ask",
    waiting: "Waiting",
    delegate: "Delegate",
    checking: "Checking",
    snapshot: "Snapshot",
    screenshot: "Screenshot",
    fetch: "Fetch",
    searchWeb: "Search web",
    searchPhotos: "Search photos",
    searchIcons: "Search icons",
    searchVideos: "Search videos",
    createImage: "Create image",
    shareFile: "Share file",
    publish: "Publish",
    buildMobile: "Build mobile app",
    appStore: "App Store",
    googlePlay: "Google Play",
    appStoreScreenshot: "Upload screenshot",
    reloadedPreview: "Reloaded preview",
    checkAccount: "Check account",
    searchedAppActions: "Searched app actions",
    checkedConnectedApps: "Checked connected apps",
    connect: "Connect",
    checkedMcpTools: "Checked MCP tools",
    loadedSkill: "Loaded skill",
    loadingSkill: "Loading skill",
    checkingAccount: "Checking account",
    memory: "Memory",
    searchedPastChats: "Searched past chats",
    creatingPlan: "Creating plan",
    updatingPlan: "Updating plan",
    reloadingPreview: "Reloading preview",
    restartedApp: "Restarted app",
  },

  memory: {
    couldntSearch: "Couldn't search memory",
    notChanged: "Memory not changed",
    remembered: "Remembered",
    remembering: "Remembering",
    updated: "Updated memory",
    updating: "Updating memory",
    forgot: "Forgot",
    forgetting: "Forgetting",
    searched: "Searched memory",
    searching: "Searching memory",
  },

  /** What a row names when the call carries no subject of its own. */
  targets: {
    shell: "shell {id}",
    backgroundShell: "background shell",
    items: plural({ one: "{n} item", other: "{n} items" }),
  },

  /** The checklist tools narrated as a row. */
  todos: {
    planning: "Planning",
    startedTodo: "Started to-do",
    completedPlan: "Completed plan",
    planned: "Planned",
    upNext: "Up next",
    updatedPlan: "Updated plan",
    steps: plural({ one: "{n} step", other: "{n} steps" }),
    task: "Task",
    done: "Done",
    started: "Started",
    update: "Update",
    /** The sticky checklist's summary row. */
    progress: "{done} of {total} Done",
  },

  /** A group of calls folded into one line: "Explored 3 files, 1 command". */
  group: {
    label: "{verb} {detail}",
    listSeparator: ", ",
    searching: "Searching",
    searched: "Searched",
    exploring: "Exploring",
    explored: "Explored",
    editing: "Editing",
    edited: "Edited",
    searchingPhotos: "Searching photos",
    searchedPhotos: "Searched photos",
    searchingIcons: "Searching icons",
    searchedIcons: "Searched icons",
    searchingVideos: "Searching videos",
    searchedVideos: "Searched videos",
    checking: "Checking",
    checked: "Checked",
    running: "Running",
    ran: "Ran",
    files: plural({ one: "{n} file", other: "{n} files" }),
    readCount: "read {n}",
    searches: plural({ one: "{n} search", other: "{n} searches" }),
    pages: plural({ one: "{n} page", other: "{n} pages" }),
    times: plural({ one: "{n} time", other: "{n} times" }),
    photoSearches: plural({ one: "{n} photo search", other: "{n} photo searches" }),
    iconSearches: plural({ one: "{n} icon search", other: "{n} icon searches" }),
    videoSearches: plural({ one: "{n} video search", other: "{n} video searches" }),
    browserChecks: plural({ one: "{n} browser check", other: "{n} browser checks" }),
    commands: plural({ one: "{n} command", other: "{n} commands" }),
    things: plural({ one: "{n} thing", other: "{n} things" }),
  },

  terminal: {
    commandFallback: "command",
    failed: "Command failed.",
    running: "Running…",
    noOutput: "No output.",
  },

  await: {
    liveWithBudget: "Waiting · {sec}s / up to {budget}s",
    live: "Waiting · {sec}s",
    budget: "Waiting · up to {budget}s",
    waiting: "Waiting…",
  },

  screenshot: {
    unavailable: "Screenshot unavailable (sandbox offline or file expired)",
    open: "Open screenshot",
    alt: "App screenshot",
    download: "Download screenshot",
  },

  subagent: {
    working: "Working",
    agent: "Agent",
    runningTool: "Running · {tool}",
    runningToolTarget: "Running · {tool} {target}",
    running: "Running…",
    runningNow: "Running",
    failed: "Failed",
    done: "Done",
    doneTools: plural({ one: "Done · {n} tool", other: "Done · {n} tools" }),
    open: "Open subagent",
    title: "Subagent",
    close: "Close subagent",
    starting: "Starting…",
    noActivity: "No activity yet.",
  },

  turn: {
    working: "Working",
    workedFor: "Worked for {duration}",
    workedForMoment: "Worked for a moment",
    waitingForConnection: "Waiting for your connection",
    finishingUp: "Finishing up",
    startingWorkspace: "Starting workspace",
    connecting: "Connecting",
    planningNext: "Planning next moves",
    empty: "The turn ended without a reply. Send another message to carry on.",
    revert: "Revert to this point",
    copyReply: "Copy reply",
    creatingImages: plural({ one: "Creating image", other: "Creating {n} images" }),
    todayAt: "Today at {time}",
    yesterdayAt: "Yesterday at {time}",
    dateAt: "{date} at {time}",
  },

  replyImage: {
    failed: "Image couldn't load · {host}",
    failedWithAlt: "Image couldn't load — {alt} · {host}",
    alt: "Image",
    open: "Open image",
  },

  plan: {
    fallbackTitle: "Plan",
    created: "Created Plan",
    building: "Building Plan",
    built: "Built Plan",
    view: "View Plan",
    build: "Build",
    buildThis: "Build this plan",
    buildingShort: "Building",
    buildingEllipsis: "Building…",
    builtShort: "Built",
    loadFailed: "Couldn't load this plan.",
    aria: "Plan",
    loading: "Loading plan…",
    noBody: "This plan has no body yet.",
    todos: "Todos",
    noTodos: "0 todos",
  },

  composerHelper: {
    connectionError: "Connection Error",
    connectionFailedCheck:
      "Connection failed. If the problem persists, please check your internet connection or VPN.",
    connectionFailedSupport:
      "Connection failed. Please try again, or contact support if the issue persists.",
    turnStopped: "Turn stopped",
    turnStoppedMessage: "That turn didn't finish. You can try again.",
    buildInProgress: "Build in progress",
    buildInProgressMessage: "Another build is still finishing. Wait a moment, then try again.",
    workspaceNotReady: "Workspace not ready",
    workspaceNotReadyMessage: "The workspace isn't ready yet. Try again in a moment.",
    somethingWentWrong: "Something went wrong",
    pleaseTryAgain: "Please try again.",
  },

  generateImage: {
    failedDefault: "Image generation didn’t finish.",
    couldntCreate: "Couldn’t create image",
    creating: "Creating image",
    open: "Open image",
    openWith: "Open image — {label}",
    openGenerated: "Open generated image",
    alt: "Generated image",
    download: "Download image",
    savedTo: "Saved to {path} — start the app to view it.",
  },

  publishCard: {
    publishing: "Publishing",
    stillPublishing: "Still publishing",
    live: "Live",
    published: "Published",
    failedDefault: "Publish didn’t finish.",
    outOfCredits: "Out of credits",
    upgradeRequired: "Upgrade required",
    failed: "Publish failed",
    seePublish: "See Publish for the live link.",
  },

  connectCard: {
    connected: "Connected. Ask again to carry on.",
    couldntStart: "Couldn't start that. Try again.",
    notYet: "Not connected yet — finish approving.",
    approveInTab: "Approve access in the new tab.",
    checking: "Checking…",
    approved: "I've approved it",
    prompt: "Connect to let the agent continue.",
    openAgainTitle: "Open the {name} approval page again",
    opening: "Opening…",
    openAgain: "Open again",
  },

  secrets: {
    title: "Add secrets",
  },

  presentedFile: {
    open: "Open {name}",
    downloadFailed: "Couldn't download. Try again.",
    typeText: "Text",
    typeFile: "File",
  },

  photos: {
    open: "Open {source} photo",
    altFor: "Stock photo for {query}",
    alt: "Stock photo",
  },

  diagram: {
    expand: "Expand",
    expandDiagram: "Expand diagram",
    label: "Diagram",
    zoomOut: "Zoom out",
    zoomIn: "Zoom in",
    resetView: "Reset view",
    hint: "Drag to pan · scroll to zoom",
    copyCode: "Copy code",
  },
};
